import { isInactive } from './is-inactive';
import type { CatalogueSelection, CatalogueUniverses } from './match';
import { type DatePreset, formatLocalDay } from './start-date-window';

export type FilterTagId =
  | { kind: 'theme' }
  | { kind: 'date' }
  | { kind: 'place'; country: string }
  | { kind: 'type'; code: string }
  | { kind: 'attendance'; code: string }
  | { kind: 'access'; code: string }
  | { kind: 'language'; code: string };

export type FilterTag = {
  key: string;
  id: FilterTagId;
  label: string;
  ariaLabel: string;
};

export type DateWindowLabel = Exclude<DatePreset, 'all' | 'day'>;

export type FilterTagLabels = {
  datePrefix: string;
  remove: string;
  theme: Record<string, string>;
  type: Record<string, string>;
  attendance: Record<string, string>;
  access: Record<string, string>;
  language: Record<string, string>;
  date: Record<DateWindowLabel, string>;
  country: Record<string, string>;
  city: Record<string, string>;
};

export const buildFilterTags = ({
  selection,
  universes,
  countryCities,
  labels,
}: {
  selection: CatalogueSelection;
  universes: CatalogueUniverses;
  countryCities: Record<string, string[]>;
  labels: FilterTagLabels;
}): FilterTag[] => {
  const {
    theme,
    types,
    attendance,
    access,
    languages,
    cities,
    datePreset,
    dateDay,
  } = selection;
  const tags: FilterTag[] = [];
  const selectedCities = new Set(cities);

  if (theme !== 'all') {
    tags.push(
      makeTag({ kind: 'theme' }, labels.theme[theme] ?? theme, labels.remove),
    );
  }

  if (cities.length > 0) {
    for (const [country, keys] of Object.entries(countryCities)) {
      const chosen = keys.filter((key) => selectedCities.has(key));
      if (chosen.length === 0) {
        continue;
      }

      const countryName = labels.country[country] ?? country;
      const cityNames = chosen.map((key) => labels.city[key] ?? key);
      tags.push(
        makeTag(
          { kind: 'place', country },
          `${countryName} : ${cityNames.join(', ')}`,
          labels.remove,
        ),
      );
    }
  }

  appendSelected(
    tags,
    universes.types,
    types,
    labels.type,
    (code) => ({ kind: 'type', code }),
    labels.remove,
  );
  appendSelected(
    tags,
    universes.attendance,
    attendance,
    labels.attendance,
    (code) => ({ kind: 'attendance', code }),
    labels.remove,
  );

  if (datePreset !== 'all' && !(datePreset === 'day' && dateDay === '')) {
    const value =
      datePreset === 'day' ? formatLocalDay(dateDay) : labels.date[datePreset];
    if (value !== '') {
      tags.push(
        makeTag(
          { kind: 'date' },
          `${labels.datePrefix} : ${value}`,
          labels.remove,
        ),
      );
    }
  }

  appendSelected(
    tags,
    universes.access,
    access,
    labels.access,
    (code) => ({ kind: 'access', code }),
    labels.remove,
  );
  appendSelected(
    tags,
    universes.languages,
    languages,
    labels.language,
    (code) => ({ kind: 'language', code }),
    labels.remove,
  );

  return tags;
};

const makeTag = (
  id: FilterTagId,
  label: string,
  remove: string,
): FilterTag => ({
  key: filterTagKey(id),
  id,
  label,
  ariaLabel: `${remove} ${label}`,
});

const filterTagKey = (id: FilterTagId): string => {
  switch (id.kind) {
    case 'theme':
    case 'date':
      return id.kind;
    case 'place':
      return `place:${id.country}`;
    case 'type':
    case 'attendance':
    case 'access':
    case 'language':
      return `${id.kind}:${id.code}`;
  }
};

const appendSelected = (
  tags: FilterTag[],
  ordered: readonly string[],
  selected: string[],
  labels: Record<string, string>,
  idFor: (code: string) => FilterTagId,
  remove: string,
): void => {
  if (isInactive(selected, ordered)) {
    return;
  }

  for (const code of ordered) {
    if (!selected.includes(code)) {
      continue;
    }

    tags.push(makeTag(idFor(code), labels[code] ?? code, remove));
  }
};
